/**
 * DROPIT e-mail popup (snippets/dropit-signup.liquid).
 * Opens once per session after a delay, 40% scroll or exit intent; joins the newsletter through Shopify's
 * customer form; shows the code and applies it to the cart.
 */

const SEEN_KEY = 'dropit:signup-seen'; // sessionStorage: shown in this session
const DONE_KEY = 'dropit:signup-done'; // localStorage: already joined on this browser
const PENDING_KEY = 'dropit:signup-pending'; // sessionStorage: native submit in flight (captcha)

/** @param {Storage} store @param {string} key */
const read = (store, key) => {
  try {
    return store.getItem(key);
  } catch {
    return null;
  }
};

/** @param {Storage} store @param {string} key @param {string} value */
const write = (store, key, value) => {
  try {
    store.setItem(key, value);
  } catch {}
};

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

class DropitSignup extends HTMLElement {
  connectedCallback() {
    this.dialog = /** @type {HTMLDialogElement} */ (this.querySelector('[data-pop]'));
    this.form = /** @type {HTMLFormElement} */ (this.querySelector('form'));
    this.code = this.dataset.code || '';
    if (!this.dialog || !this.form) return;

    this.addEventListener('click', this.#onClick);
    this.form.addEventListener('submit', this.#onSubmit);
    this.dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      this.close();
    });
    // Click on the backdrop closes
    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.close();
    });

    // Came back from a native submit (captcha) or Shopify says the form was posted
    if (this.querySelector('[data-pop-posted]') || read(sessionStorage, PENDING_KEY)) {
      try {
        sessionStorage.removeItem(PENDING_KEY);
      } catch {}
      if (this.querySelector('[data-pop-posted]')) {
        this.#done();
        this.open(true);
        return;
      }
    }

    // Test mode (theme setting): every page load, ignoring what this browser already saw
    this.test = this.hasAttribute('data-test');
    if (!this.test && (read(localStorage, DONE_KEY) || read(sessionStorage, SEEN_KEY))) return;
    this.#arm();
  }

  #arm() {
    const fire = () => {
      cleanup();
      this.#openWhenFree();
    };
    const delay = Number(this.dataset.delay) || 6000;
    const timer = window.setTimeout(fire, delay);

    // 40% of the page scrolled (Horizon scrolls .page-wrapper on desktop, so listen in capture)
    const onScroll = () => {
      const scroller = document.querySelector('.page-wrapper');
      const el = scroller && scroller.scrollHeight > scroller.clientHeight ? scroller : document.documentElement;
      const depth = (el.scrollTop + el.clientHeight) / el.scrollHeight;
      if (depth > 0.4 && performance.now() > 2500) fire();
    };
    // Exit intent: pointer leaves through the top of the window (desktop)
    /** @param {MouseEvent} event */
    const onLeave = (event) => {
      if (!event.relatedTarget && event.clientY <= 0) fire();
    };

    document.addEventListener('scroll', onScroll, { capture: true, passive: true });
    document.documentElement.addEventListener('mouseleave', onLeave);

    const cleanup = () => {
      window.clearTimeout(timer);
      document.removeEventListener('scroll', onScroll, { capture: true });
      document.documentElement.removeEventListener('mouseleave', onLeave);
    };
  }

  /** Waits until no drawer, menu or other dialog is open */
  #openWhenFree() {
    const busy = () =>
      document.querySelector('dialog[open]:not([data-pop]), .menu-drawer-container[open], details[open].menu-drawer-container');
    if (!busy()) {
      this.open();
      return;
    }
    const check = window.setInterval(() => {
      if (busy()) return;
      window.clearInterval(check);
      window.setTimeout(() => this.open(), 800);
    }, 1000);
  }

  /** @param {boolean} [force] */
  open(force = false) {
    if (this.dialog.open) return;
    if (!force && !this.test && read(sessionStorage, SEEN_KEY)) return;
    write(sessionStorage, SEEN_KEY, '1');
    this.dialog.showModal();
    // Don't pop the keyboard on phones: focus the dialog, not the field
    if (window.matchMedia('(pointer: fine)').matches && !this.dialog.querySelector('[data-step="form"]')?.hidden) {
      /** @type {HTMLInputElement | null} */ (this.form.querySelector('input[type="email"]'))?.focus();
    } else {
      this.dialog.focus();
    }
  }

  close() {
    if (!this.dialog.open || this.dialog.hasAttribute('data-closing')) return;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) {
      this.dialog.close();
      return;
    }
    this.dialog.setAttribute('data-closing', '');
    window.setTimeout(() => {
      this.dialog.removeAttribute('data-closing');
      this.dialog.close();
    }, 240);
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    if (target.closest('[data-pop-close]')) this.close();
    else if (target.closest('[data-pop-copy]')) this.#copy();
    else if (target.closest('[data-pop-apply]')) this.#apply();
  };

  /** @param {SubmitEvent} event */
  #onSubmit = async (event) => {
    event.preventDefault();
    const input = /** @type {HTMLInputElement} */ (this.form.querySelector('input[type="email"]'));
    const error = /** @type {HTMLElement} */ (this.querySelector('[data-pop-error]'));
    const button = /** @type {HTMLButtonElement} */ (this.querySelector('[data-pop-submit]'));
    const email = input.value.trim();

    if (!EMAIL.test(email)) {
      input.setAttribute('aria-invalid', 'true');
      error.textContent = 'Confira o e-mail: parece que falta alguma coisa.';
      error.hidden = false;
      input.focus();
      return;
    }
    input.removeAttribute('aria-invalid');
    error.hidden = true;
    button.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(this.form.action, {
        method: 'POST',
        body: new FormData(this.form),
        headers: { Accept: 'text/html' },
        credentials: 'same-origin',
      });
      // Shopify wants a captcha: send the real form, the page comes back with the code open
      if (response.url.includes('/challenge')) {
        write(sessionStorage, PENDING_KEY, '1');
        HTMLFormElement.prototype.submit.call(this.form);
        return;
      }
      if (!response.ok && response.status !== 302) throw new Error(String(response.status));
      this.#done();
    } catch {
      // Network trouble: don't lose the lead, try the native submit
      write(sessionStorage, PENDING_KEY, '1');
      HTMLFormElement.prototype.submit.call(this.form);
    } finally {
      button.removeAttribute('aria-busy');
    }
  };

  #done() {
    write(localStorage, DONE_KEY, '1');
    const form = /** @type {HTMLElement} */ (this.querySelector('[data-step="form"]'));
    const done = /** @type {HTMLElement} */ (this.querySelector('[data-step="done"]'));
    form.hidden = true;
    done.hidden = false;
    this.dialog.setAttribute('aria-labelledby', '');
    this.dialog.setAttribute('aria-label', 'Seu cupom de desconto');
    /** @type {HTMLElement | null} */ (done.querySelector('[data-pop-apply]'))?.focus({ preventScroll: true });
    window.Shopify?.analytics?.publish?.('dropit_newsletter_signup', { source: 'popup' });
  }

  async #copy() {
    const label = this.querySelector('[data-pop-copy-label]');
    try {
      await navigator.clipboard.writeText(this.code);
      if (label) {
        const before = label.innerHTML;
        label.textContent = 'Copiado';
        window.setTimeout(() => (label.innerHTML = before), 1600);
      }
    } catch {}
  }

  async #apply() {
    const button = /** @type {HTMLButtonElement} */ (this.querySelector('[data-pop-apply]'));
    const note = /** @type {HTMLElement} */ (this.querySelector('[data-pop-applied]'));
    const root = window.Shopify?.routes?.root || '/';
    button.setAttribute('aria-busy', 'true');

    try {
      // Sets the discount cookie used by the cart and the checkout
      await fetch(`${root}discount/${encodeURIComponent(this.code)}`, { credentials: 'same-origin' });
      await fetch(`${root}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ discount: this.code }),
      });

      // Applied: confirm on the button for a moment, then get out of the way
      const label = this.querySelector('[data-pop-apply-label]');
      if (label) label.textContent = 'Cupom aplicado';
      window.setTimeout(() => this.close(), 900);
    } catch {
      note.textContent = `Não deu para aplicar agora. Use o código ${this.code} no carrinho ou no checkout.`;
      note.hidden = false;
    } finally {
      button.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('dropit-signup')) customElements.define('dropit-signup', DropitSignup);
